import React, {useState, useEffect, useRef} from 'react';
import {Button, Dimmer, Form, Input, Loader, Modal, Segment, Dropdown} from "semantic-ui-react";
import {toast} from "react-toastify";
import {updateMassUploadParcel} from '../../../services/massUploadApi';
import {apiPost} from '../../../services/apiClient';
import DatePicker from '../../../components/DatePicker/DatePicker';
import {parse, startOfToday} from '../../../services/dateUtils';

/**
 * Модалка редактирования отправления массовой загрузки
 * С поддержкой suggest для адресов и автоматической валидацией
 */
const EditParcelModal = ({open, parcel, onClose, onSave, column}) => {
    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null); // Выбранный адрес из dropdown
    const [isAddressManuallyEdited, setIsAddressManuallyEdited] = useState(false); // Флаг ручного редактирования
    const [excludedDates, setExcludedDates] = useState([]); // Выходные и праздничные дни для календаря
    const [loadingDayOffs, setLoadingDayOffs] = useState(false);
    const suggestionTimeoutRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (suggestionTimeoutRef.current) {
                clearTimeout(suggestionTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (open && parcel && column && mountedRef.current) {
            let initialValue = parcel.data[column.fieldName] || '';
            
            // Для даты отгрузки нормализуем формат
            if (isDepartureDateField(column.fieldName) && initialValue) {
                // Если это Date объект, преобразуем в строку формата dd.MM.yyyy
                if (initialValue instanceof Date) {
                    if (!isNaN(initialValue.getTime())) {
                        const day = String(initialValue.getDate()).padStart(2, '0');
                        const month = String(initialValue.getMonth() + 1).padStart(2, '0');
                        const year = initialValue.getFullYear();
                        initialValue = `${day}.${month}.${year}`;
                    } else {
                        initialValue = '';
                    }
                } else if (typeof initialValue === 'string') {
                    // Если это строка, проверяем формат и нормализуем
                    const dateStr = initialValue.trim();
                    // Если формат YYYY-MM-DD, преобразуем в dd.MM.yyyy
                    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                        const [year, month, day] = dateStr.split('-');
                        initialValue = `${day}.${month}.${year}`;
                    }
                    // Если формат уже dd.MM.yyyy или dd/MM/yyyy, оставляем как есть (но нормализуем слеши)
                    else if (/^\d{1,2}[./]\d{1,2}[./]\d{2,4}$/.test(dateStr)) {
                        // Заменяем слеши на точки для единообразия
                        initialValue = dateStr.replace(/\//g, '.');
                    }
                }
            }
            
            setValue(initialValue);
            
            // Если это адресное поле и адрес уже нормализован, создаем объект selectedAddress
            if (isAddressField(column.fieldName) && initialValue) {
                // Создаем объект адреса из сохраненного значения (оно уже нормализовано)
                setSelectedAddress({
                    fullName: initialValue,
                    addressName: initialValue,
                    name: initialValue
                });
                setIsAddressManuallyEdited(false);
            } else {
                setSelectedAddress(null);
                setIsAddressManuallyEdited(false);
            }
            
            // Загружаем выходные дни для календаря даты отгрузки
            if (isDepartureDateField(column.fieldName)) {
                loadDayOffs();
            }
        }
    }, [open, parcel, column]);
    
    const loadDayOffs = () => {
        if (loadingDayOffs) return;
        
        setLoadingDayOffs(true);
        const today = new Date();
        const endDate = new Date();
        endDate.setFullYear(today.getFullYear() + 1); // Загружаем на год вперед
        
        apiPost('ref/dayoffs/list', {
            startDate: today.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        })
            .then(data => {
                if (!mountedRef.current) return;
                if (data && data.entries) {
                    // Преобразуем строки дат в объекты Date
                    const dates = data.entries.map(dateStr => new Date(dateStr));
                    setExcludedDates(dates);
                }
            })
            .catch(err => {
                if (!mountedRef.current) return;
                console.error('Ошибка загрузки выходных дней:', err);
                // В случае ошибки исключаем только выходные (суббота, воскресенье)
                setExcludedDates([]);
            })
            .finally(() => {
                if (mountedRef.current) {
                    setLoadingDayOffs(false);
                }
            });
    };
    
    // Функция для фильтрации дат: исключаем выходные и праздничные дни
    const filterDate = (date) => {
        if (!date) return false;
        
        const dayOfWeek = date.getDay();
        // Исключаем субботу (6) и воскресенье (0)
        if (dayOfWeek === 0 || dayOfWeek === 6) {
            return false;
        }
        
        // Исключаем праздничные дни
        const dateStr = date.toISOString().split('T')[0];
        const isHoliday = excludedDates.some(holiday => {
            const holidayStr = holiday.toISOString().split('T')[0];
            return holidayStr === dateStr;
        });
        
        if (isHoliday) {
            return false;
        }
        
        // Дата должна быть не раньше сегодняшнего дня
        const today = startOfToday();
        
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        
        return checkDate >= today;
    };

    const isAddressField = (fieldName) => {
        return fieldName === 'senderAddress' || fieldName === 'recipientAddress';
    };

    const isParcelTypeField = (fieldName) => {
        return fieldName === 'parcelType';
    };
    
    const isDepartureDateField = (fieldName) => {
        return fieldName === 'departureDate';
    };

    const parcelTypeOptions = [
        { key: 'document', value: 'Конверт', text: 'Конверт (Документы)' },
        { key: 'cargo', value: 'Груз', text: 'Груз' }
    ];

    const fetchAddressSuggestions = (text) => {
        if (!text || text.length < 3) {
            if (mountedRef.current) {
                setSuggestions([]);
                setShowSuggestions(false);
            }
            return;
        }

        if (suggestionTimeoutRef.current) {
            clearTimeout(suggestionTimeoutRef.current);
        }

        suggestionTimeoutRef.current = setTimeout(() => {
            apiPost('parcels/addresses/suggestions', { text, maxCount: 10 })
                .then(data => {
                    if (!mountedRef.current) return;
                    if (data && data.entries) {
                        setSuggestions(data.entries);
                        setShowSuggestions(true);
                    }
                })
                .catch(() => {
                    if (!mountedRef.current) return;
                    setSuggestions([]);
                    setShowSuggestions(false);
                });
        }, 300);
    };

    const handleValueChange = (newValue) => {
        if (!mountedRef.current) return;
        
        // Для даты отгрузки newValue может быть объектом Date или строкой
        if (column && isDepartureDateField(column.fieldName)) {
            if (newValue instanceof Date) {
                // Проверяем валидность даты
                if (!isNaN(newValue.getTime())) {
                    // Используем локальное время для форматирования даты, чтобы избежать проблем с часовыми поясами
                    const year = newValue.getFullYear();
                    const month = String(newValue.getMonth() + 1).padStart(2, '0');
                    const day = String(newValue.getDate()).padStart(2, '0');
                    setValue(`${year}-${month}-${day}`);
                } else {
                    setValue('');
                }
            } else if (newValue === null || newValue === undefined) {
                setValue('');
            } else {
                setValue(newValue || '');
            }
            return;
        }
        
        setValue(newValue);
        
        // Если адрес был выбран из dropdown, но потом изменен вручную - сбрасываем выбор
        if (column && isAddressField(column.fieldName)) {
            if (selectedAddress && newValue !== (selectedAddress.fullName || selectedAddress.addressName || selectedAddress.name || '')) {
                setSelectedAddress(null);
                setIsAddressManuallyEdited(true);
            }
            fetchAddressSuggestions(newValue);
        }
    };

    const handleAddressSelect = (address) => {
        if (!mountedRef.current) return;
        const fullAddress = address.fullName || address.addressName || address.name || '';
        setValue(fullAddress);
        setSelectedAddress(address); // Сохраняем выбранный адрес
        setIsAddressManuallyEdited(false); // Сбрасываем флаг ручного редактирования
        setShowSuggestions(false);
        setSuggestions([]);
    };

    const handleSave = () => {
        if (!parcel || !column || !mountedRef.current) return;

        // Для адресных полей проверяем, что адрес выбран из dropdown
        if (isAddressField(column.fieldName)) {
            if (!selectedAddress && value.trim()) {
                toast.error('Пожалуйста, выберите адрес из списка предложений. Нельзя сохранить адрес, введенный вручную.');
                return;
            }
        }

        setLoading(true);
        
        // Для адресных полей используем адрес из dropdown (он уже нормализован)
        if (isAddressField(column.fieldName) && selectedAddress) {
            // Адрес выбран из dropdown - используем его напрямую (он уже нормализован)
            const addressToSave = selectedAddress.fullName || selectedAddress.addressName || selectedAddress.name || value;
            saveParcelData(addressToSave);
        } else {
            // Для неадресных полей сохраняем сразу
            saveParcelData(value);
        }
    };

    const saveParcelData = (fieldValue) => {
        if (!mountedRef.current) return;
        
        // Для даты отгрузки преобразуем Date в строку формата dd.MM.yyyy (с точками)
        // Это предотвращает путаницу между форматами dd/MM/yyyy и MM/dd/yyyy
        let valueToSave = fieldValue;
        if (column && isDepartureDateField(column.fieldName)) {
            if (fieldValue instanceof Date) {
                // Используем локальное время для форматирования даты, чтобы избежать проблем с часовыми поясами
                const year = fieldValue.getFullYear();
                const month = String(fieldValue.getMonth() + 1).padStart(2, '0');
                const day = String(fieldValue.getDate()).padStart(2, '0');
                // Сохраняем в формате dd.MM.yyyy (с точками) для единообразия
                valueToSave = `${day}.${month}.${year}`;
            } else if (typeof fieldValue === 'string' && fieldValue.trim()) {
                // Если это строка, нормализуем формат
                const dateStr = fieldValue.trim();
                // Если формат YYYY-MM-DD, преобразуем в dd.MM.yyyy
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                    const [year, month, day] = dateStr.split('-');
                    valueToSave = `${day}.${month}.${year}`;
                }
                // Если формат со слешами, заменяем на точки
                else if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(dateStr)) {
                    valueToSave = dateStr.replace(/\//g, '.');
                }
                // Если уже в формате с точками, оставляем как есть
                else if (/^\d{1,2}\.\d{1,2}\.\d{2,4}$/.test(dateStr)) {
                    valueToSave = dateStr;
                }
            }
        }
        
        // Находим позиции в parcel.data (могут быть под ключом 'positions', 'Positions' и т.д.)
        let positionsArray = null;
        if (parcel && parcel.data) {
            // Сначала проверяем стандартное имя 'positions' (camelCase)
            if (parcel.data.positions && Array.isArray(parcel.data.positions)) {
                positionsArray = parcel.data.positions;
            }
            // Если не найдено, ищем по другим возможным ключам
            else {
                const keys = Object.keys(parcel.data);
                for (let k of keys) {
                    const v = parcel.data[k];
                    if (Array.isArray(v) && v.length >= 0) {
                        const sample = v[0] || {};
                        // Проверяем, что это массив позиций по наличию характерных полей
                        if (['weight','weightPerPlace','packagesCount','length','width','height','positionNumber'].some(p => p in sample)) {
                            positionsArray = v;
                            break;
                        }
                    }
                }
            }
        }
        
        // Создаем updatedData с сохранением всех данных из parcel.data, включая позиции (если они есть)
        // ВАЖНО: Явно добавляем positions в updatedData, чтобы они не терялись при сохранении даты
        const updatedData = {
            ...parcel.data,
            rowNumber: parcel.rowNumber,
            [column.fieldName]: valueToSave
        };
        
        // Явно добавляем positions в updatedData, если они есть
        // Используем стандартное имя 'positions' (camelCase) для согласованности с бэком
        // Это гарантирует, что positions не будут потеряны при сохранении даты
        if (positionsArray && Array.isArray(positionsArray)) {
            updatedData.positions = positionsArray;
        }

        // Определяем имя поля для оптимизации валидации (только для адресов)
        const fieldNameForValidation = column.fieldName;
        updateMassUploadParcel(parcel.id, updatedData, fieldNameForValidation)
            .then(() => {
                if (!mountedRef.current) return;
                toast.success('Данные сохранены');
                
                // Сначала сбрасываем loading, ПОТОМ закрываем модалку
                setLoading(false);
                
                // Передаем обновленное значение в callback для локального обновления
                onSave && onSave(parcel.id, column.fieldName, valueToSave);
                onClose();
            })
            .catch(err => {
                if (!mountedRef.current) return;
                console.error(err);
                
                // Обработка ошибок валидации
                let errorMessage = 'Ошибка при сохранении';
                if (err && typeof err === 'object') {
                    if (err.Error) {
                        errorMessage = err.Error;
                    } else if (err.error) {
                        errorMessage = err.error;
                    } else if (err.message) {
                        errorMessage = err.message;
                    }
                }
                
                toast.error(errorMessage);
                setLoading(false);
            });
    };

    const handleClose = () => {
        if (mountedRef.current) {
            setValue('');
            setSuggestions([]);
            setShowSuggestions(false);
            setSelectedAddress(null);
            setIsAddressManuallyEdited(false);
            setExcludedDates([]);
        }
        onClose();
    };

    if (!column) return null;

    const isAddress = isAddressField(column.fieldName);
    const isParcelType = isParcelTypeField(column.fieldName);
    const isDepartureDate = isDepartureDateField(column.fieldName);
    
    // Для даты отгрузки преобразуем строку в Date объект
    let departureDateValue = null;
    if (isDepartureDate && value) {
        if (value instanceof Date) {
            departureDateValue = isNaN(value.getTime()) ? null : value;
        } else if (typeof value === 'string' && value.trim()) {
            // Пробуем разные форматы даты
            const dateStr = value.trim();
            let date = null;
            
            // Формат YYYY-MM-DD
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                date = new Date(dateStr + 'T00:00:00');
            } 
            // Формат DD.MM.YYYY или DD/MM/YYYY - используем правильный парсинг для избежания путаницы с MM/dd
            else if (/^\d{1,2}[./]\d{1,2}[./]\d{2,4}$/.test(dateStr)) {
                // ВАЖНО: Всегда парсим как dd.MM.yyyy, чтобы избежать путаницы с MM/dd
                // Нормализуем: заменяем слеши на точки
                const normalizedStr = dateStr.replace(/\//g, '.');
                
                // Сначала пробуем через parse из dateUtils
                date = parse(normalizedStr, 'dd.MM.yyyy') || 
                       parse(normalizedStr, 'd.MM.yyyy') ||
                       parse(normalizedStr, 'dd.M.yyyy') ||
                       parse(normalizedStr, 'd.M.yyyy') ||
                       parse(normalizedStr, 'dd.MM.yy') ||
                       parse(normalizedStr, 'd.MM.yy');
                
                // Если парсинг не удался, используем ручной парсинг с явным указанием формата dd.MM.yyyy
                if (!date || isNaN(date.getTime())) {
                    const parts = normalizedStr.split('.');
                    if (parts.length === 3) {
                        const day = parseInt(parts[0], 10);
                        const month = parseInt(parts[1], 10);
                        let year = parseInt(parts[2], 10);
                        
                        // Проверяем валидность: день должен быть 1-31, месяц 1-12
                        if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
                            // Используем полный год (если двузначный, добавляем 2000)
                            const fullYear = year < 100 ? 2000 + year : year;
                            // ВАЖНО: Создаем Date с явным указанием: год, месяц-1, день
                            // Это гарантирует правильную интерпретацию (не MM/dd, а dd/MM)
                            date = new Date(fullYear, month - 1, day);
                        }
                    }
                }
            }
            // Пробуем стандартный парсинг только если не удалось распарсить выше
            if (!date || isNaN(date.getTime())) {
                date = new Date(dateStr);
            }
            
            departureDateValue = date && !isNaN(date.getTime()) ? date : null;
        }
    }
    
    // Минимальная дата - завтрашний день
    const today = startOfToday();

    return (
        <Modal
            closeIcon
            onClose={handleClose}
            open={open}
            size="small"
        >
            <Modal.Header>
                Изменить поле "{column.title}" для {parcel && parcel.data && parcel.data.sequentialNumber ? `№ ПП ${parcel.data.sequentialNumber}` : 'отправления'}
            </Modal.Header>

            <Segment vertical style={{borderBottom: 'none'}}>
                <Dimmer active={loading} inverted>
                    <Loader size='medium'/>
                </Dimmer>
                <Modal.Content className='modal__content'>
                    <Form>
                        <Form.Field>
                            <label>{column.title}</label>
                            {isAddress && (
                                <div style={{marginBottom: '8px', fontSize: '12px', color: '#666'}}>
                                    <i className="fa fa-info-circle"></i> Для сохранения адреса необходимо выбрать его из списка предложений
                                </div>
                            )}
                            <div style={{position: 'relative'}}>
                                {isParcelType ? (
                                    <Dropdown
                                        fluid
                                        selection
                                        options={parcelTypeOptions}
                                        value={value}
                                        onChange={(e, {value}) => handleValueChange(value)}
                                        placeholder="Выберите тип отправления"
                                    />
                                ) : isDepartureDate ? (
                                    <DatePicker
                                        name="departureDate"
                                        value={departureDateValue && !isNaN(departureDateValue.getTime()) ? departureDateValue : null}
                                        onChange={(e, {value}) => handleValueChange(value)}
                                        filterDate={filterDate}
                                        minDate={today}
                                        dateFormat="dd.MM.yyyy"
                                        placeholderText="дд.мм.гггг"
                                        clearable={true}
                                        autoFocus
                                    />
                                ) : (
                                    <Input 
                                        fluid
                                        value={value}
                                        onChange={(e, {value}) => handleValueChange(value)}
                                        placeholder={`Введите ${column.title}`}
                                        autoFocus
                                        error={isAddress && isAddressManuallyEdited && !selectedAddress && value.trim() ? {
                                            content: 'Выберите адрес из списка предложений',
                                            pointing: 'below'
                                        } : false}
                                    />
                                )}
                                
                                {/* Suggestions для адресов */}
                                {isAddress && showSuggestions && suggestions.length > 0 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        zIndex: 1000,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                    }}>
                                        {suggestions.map((addr, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    borderBottom: idx < suggestions.length - 1 ? '1px solid #eee' : 'none'
                                                }}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    handleAddressSelect(addr);
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                                            >
                                                <div style={{fontWeight: '500'}}>{addr.fullName}</div>
                                                {addr.description && (
                                                    <div style={{fontSize: '12px', color: '#666', marginTop: '2px'}}>
                                                        {addr.description}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Form.Field>
                    </Form>
                </Modal.Content>
            </Segment>

            <Modal.Actions className='modal__actions'>
                <Button onClick={handleClose}>
                    Отмена
                </Button>
                <Button 
                    disabled={loading || (isAddress && !selectedAddress && !!value.trim())} 
                    onClick={handleSave} 
                    className='modal__actions_yes'
                    title={isAddress && !selectedAddress && !!value.trim() ? 'Выберите адрес из списка предложений' : ''}
                >
                    Сохранить
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default EditParcelModal;




// WEBPACK FOOTER //
// ./App/pages/massUpload/components/EditParcelModal.jsx